import React, { useState, useEffect } from "react";
import { FaGlobe, FaDollarSign } from "react-icons/fa";
import { useCountries } from "../hooks/useCountries";
import { useCurrencies } from "../hooks/useCurrencies";
import { getCurrencyForCountry } from "../utils/countryCurrencyMap";
import { getUser } from "../services/localService";
import { useSelector } from "react-redux";
import logger from "../utils/logger";


/**
 * Component to display user's country and currency
 * Loads from Redux state first, then allows testing with debug dropdown
 */
const CountryCurrencyDisplay = () => {
  const { countries } = useCountries();
  const { currencies } = useCurrencies();

  // Get countries from Redux state
  const countriesFromRedux = useSelector(state => state.dashboard?.countries || []);

  const [userCountry, setUserCountry] = useState(null);
  const [userCurrency, setUserCurrency] = useState(null);
  const [testCountryId, setTestCountryId] = useState(null);

  useEffect(() => {
    // Check for debug test country on mount
    const debugTestCountry = localStorage.getItem("debug_test_country");
    if (debugTestCountry) {
      setTestCountryId(parseInt(debugTestCountry));
    }
  }, []);

  useEffect(() => {
    loadCountryAndCurrency();
  }, [countriesFromRedux, currencies, testCountryId]);

  const loadCountryAndCurrency = () => {
    const currentUser = getUser();

    // Priority: testCountryId > user.countryId > organization.countryId
    const countryId = testCountryId || currentUser?.countryId || currentUser?.organization?.countryId;

    if (!countryId) {
      const kesCurrency = currencies?.find(c => c.code === "KES");
      setUserCountry({ name: "Kenya", code: "KE" });
      setUserCurrency(kesCurrency || { code: "KES", symbol: "KSh" });
      return;
    }

    // Use Redux state countries
    const country = countriesFromRedux?.find(c => c.id === countryId);

    if (country) {
      const currencyCode = getCurrencyForCountry(country.code);
      const currency = currencies?.find(c => c.code === currencyCode);

      setUserCountry(country);
      setUserCurrency(currency || { code: currencyCode, symbol: currencyCode });
    } else {
      const kesCurrency = currencies?.find(c => c.code === "KES");
      setUserCountry(null);
      setUserCurrency(kesCurrency || { code: "KES", symbol: "KSh" });
    }
  };

  const handleTestCountryChange = e => {
    const countryId = e.target.value ? parseInt(e.target.value) : null;

    logger.log("gjfkkrjejrjr [CountryCurrencyDisplay] 🧪 Test country changed to:", countryId);

    if (countryId) {
      localStorage.setItem("debug_test_country", countryId.toString());
    } else {
      localStorage.removeItem("debug_test_country");
    }

    setTestCountryId(countryId);
    setTimeout(() => {
      window.dispatchEvent(new Event("currencyChanged"));
    }, 0);
  };

  return (
    <>
      {/* TEST: Country Selector (localhost only) */}
      {window.location.hostname === "localhost" && (
        <div className="md:col-span-2 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="mb-2 text-sm font-medium text-yellow-800">
            🧪 TEST MODE: Change country to see currency update
          </p>
          <select
            value={testCountryId || ""}
            onChange={handleTestCountryChange}
            className="w-full px-3 py-2 text-sm border border-yellow-300 rounded-md focus:ring-yellow-500 focus:border-yellow-500"
          >
            <option value="">
              Use actual user country (ID: {getUser()?.countryId || getUser()?.organization?.countryId || "none"})
            </option>
            {countriesFromRedux?.map(country => (
              <option key={country.id} value={country.id}>
                {country.name} ({country.code}) [ID: {country.id}] → {getCurrencyForCountry(country.code)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Country and Currency Display */}
      <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            <FaGlobe className="inline mr-1" />
            Country
          </label>
          <div className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-300 rounded-md text-gray-700">
            {userCountry?.name || "Kenya"}
          </div>
        </div>
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            <FaDollarSign className="inline mr-1" />
            Currency
          </label>
          <div className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-300 rounded-md text-gray-700 font-mono">
            {userCurrency?.code || "KES"} {userCurrency?.symbol && `(${userCurrency.symbol})`}
          </div>
        </div>
      </div>
    </>
  );
};

export default CountryCurrencyDisplay;
